/**
 * Spec №11 (11.3.2) — App Shell declared RECORD view renderer (the IS plane).
 * Renders a row from its ViewNodeConfig layout: a metric strip + field_section
 * blocks (layout templates standard | document | compact). Related lists live in
 * the four-plane page's other planes; a timeline block is handled by HISTORY.
 * Falls back to sensible defaults (metric-role fields + a Details section) when
 * the type ships no record view.
 *
 * №13.1 (§1) — the field-section value cells are now EDITABLE in place
 * (`InlineFieldCell`): click-to-edit, optimistic commit with rollback, a "Saved ✓"
 * flash, and a governed-lock render for frozen fields. The metric strip stays
 * read-only (M5 — derived values, never fields).
 *
 * №23 Stage C (part 1, D-MS9 precondition) — REGION-AWARE + CARD-PER-SECTION.
 * `ViewBlock.region` was declared and authored (`contact`'s three sidebar
 * sections) but never read — every section rendered as a sub-headed group
 * inside ONE shared card. Now: declared sections partition by `region`
 * ("sidebar" opts in, everything else — including undeclared — is "main"), a
 * `region` prop lets a caller mount this component once per region, and each
 * section renders as its OWN full-width `.card` with its name as that card's
 * `.card__h` (matching the `keyDatesFullHtml()` mockup shape), not a
 * `.grouptitle` inside a shared box. R7 ③ (№46) applies to the new per-card
 * shape too: a section whose fields are all empty renders no card at all —
 * otherwise the split would produce an empty box where there used to be an
 * unremarkable blank row inside a bigger one.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type {
  EntityRecord,
  EntityTypeDefinition,
  ResolvedRef,
  RowDetailResult,
  ViewBlock,
  ViewNodeConfig,
} from "../../../lib/types";
import { useUpdateEntity } from "../../../lib/hooks/use-kuvio";
import { queryKeys } from "../../../lib/query-keys";
import { showToast } from "../../components/primitives";
import { columnsByName, fieldDisplay, sentenceLabel } from "../../lib/declared-view";
import { keyDateBadge, keyDateState } from "../../lib/key-dates";
import { governanceFor } from "./governance-map";
import { InlineFieldCell } from "./InlineFieldCell";
import { money } from "../../components/ledger/format";
import type { EffectiveRateRow, MatterTeamRow } from "../../hooks/use-time-billing";

const ACCENTS = ["bal--trust", "bal--wip", "bal--ar"];
// №23 Stage C3 (D-MS3 rev) — the same badge-by-scope mapping the locked
// reference (`tests/snapshots/matter-mockups.ts` `SOURCE_BADGE`) used, now
// real: `resolveRate`'s own `scope` (D-MS3: "keep it, surface it").
const RATE_SOURCE_BADGE: Record<string, string> = { matter: "badge--fixed", client: "badge--tm", firm: "badge--soft" };
function rateSourceLabel(row: EffectiveRateRow): { cls: string; label: string } {
  if (!row.scope) return { cls: "badge badge--soft", label: "No rate resolves" };
  const kind = row.scope === "matter" ? "Matter override" : row.scope === "client" ? "Client rate" : "Firm default";
  const note = row.source_note ? ` (${row.source_note})` : "";
  return { cls: `badge ${RATE_SOURCE_BADGE[row.scope] ?? "badge--soft"}`, label: `${kind} · ${row.card_id ?? ""}${note}` };
}
function teamInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}
const HEADER_ROLES = new Set(["title", "subtitle", "badge", "metric", "image"]);

function commitErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Couldn't save. Please try again.";
}

export function RecordView({
  entityType,
  entity,
  forwardRefs,
  viewConfig,
  region = "main",
  rateLadder,
  team,
}: {
  entityType: EntityTypeDefinition;
  entity: EntityRecord;
  forwardRefs?: Record<string, ResolvedRef[]>;
  viewConfig?: ViewNodeConfig;
  /** №23 Stage C p1 — which partition of declared sections to render. A caller
   *  mounts this twice (once per region) to place "sidebar" sections in its own
   *  rail; the default keeps every existing call site's behaviour for a type
   *  with no declared `region` (everything is "main"). */
  region?: "main" | "sidebar";
  /** №23 Stage C3 (D-MS3 rev/D-MS9) — data for a `variant: "rates"` section.
   *  Caller-fetched (`get_effective_rates_for_matter`), not entity fields —
   *  same reason `forwardRefs` is a prop rather than read off `entity`. */
  rateLadder?: EffectiveRateRow[];
  /** №23 Stage C3 (D-MS4 rev/D-MS9) — data for a `variant: "team"` section
   *  (`list_matter_team`). */
  team?: MatterTeamRow[];
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const update = useUpdateEntity(entityType);

  const cols = columnsByName(entityType);
  const fieldByName = Object.fromEntries(entityType.fields.map((f) => [f.name, f]));
  const layout = viewConfig?.layout;
  const oneCol = layout?.template === "compact";

  // §1 — governance + status context. Only treat a field as the status field
  // when the type actually has a workflow (N2: status is never inline-edited).
  const statusField = entityType.workflow ? entityType.workflow.field ?? "status" : undefined;
  const statusVal = statusField ? (entity as Record<string, unknown>)[statusField] : undefined;
  const governRule = governanceFor(entityType.slug, statusVal);

  // §1 — optimistic commit with rollback. Write the new value into the cached
  // row-detail immediately; on error, restore the prior snapshot + surface an
  // inline error toast. `expected_version` guards against clobbering a stale edit.
  async function commitField(fieldName: string, value: unknown): Promise<void> {
    const key = queryKeys.rowDetail(entityType.slug, entity.id);
    const prev = qc.getQueryData<RowDetailResult>(key);
    qc.setQueryData<RowDetailResult>(key, (old) =>
      old ? { ...old, entity: { ...old.entity, [fieldName]: value } as EntityRecord } : old,
    );
    try {
      await update.mutateAsync({
        id: entity.id,
        fields: { [fieldName]: value },
        expectedVersion: entity._meta?.version,
      });
    } catch (err) {
      if (prev) qc.setQueryData(key, prev);
      showToast(commitErrorMessage(err), "error", true);
      throw err;
    }
  }

  const onOpenRef = (slug: string, refId: string) => navigate(`/app/place/${slug}/${refId}`);

  // Metric strip: layout.metrics, else a metric_strip block, else metric-role fields.
  const metricBlock = layout?.blocks?.find((b) => b.type === "metric_strip");
  const metrics = (layout?.metrics ?? metricBlock?.fields ??
    entityType.fields.filter((f) => f.display_role === "metric").map((f) => f.name)).filter(
    (m) => fieldByName[m],
  );

  // Field sections: declared field_section blocks, else one Details section of
  // the non-header display fields. An undeclared `region` is "main" — nothing
  // that doesn't opt into "sidebar" moves.
  const declaredSections = (layout?.blocks?.filter((b) => b.type === "field_section") ?? []) as ViewBlock[];
  const fallbackFields = entityType.fields
    .filter((f) => !HEADER_ROLES.has(f.display_role ?? "") && f.type !== "richtext" && f.type !== "document")
    .map((f) => f.name);
  const allSections: ViewBlock[] = declaredSections.length
    ? declaredSections
    : fallbackFields.length
      ? [{ type: "field_section", name: "Details", fields: fallbackFields }]
      : [];
  const sections = allSections.filter((sec) =>
    region === "sidebar" ? sec.region === "sidebar" : sec.region !== "sidebar",
  );

  // R7 ③ / №46 — a section with nothing to say is ABSENT, not empty. That was
  // never a per-section concern while every section shared one card; now that
  // each is its own bordered box, an all-empty one must not render at all.
  // №23 Stage C3 — "rates"/"team" sections have no `fields` (caller-fetched
  // data, `rateLadder`/`team` props) — content is judged on THOSE, not on an
  // empty `sec.fields` array (which would always read as "nothing to say").
  const sectionHasContent = (sec: ViewBlock): boolean => {
    if (sec.variant === "rates") return (rateLadder ?? []).length > 0;
    if (sec.variant === "team") return (team ?? []).length > 0;
    return (sec.fields ?? []).some((fn) => {
      const f = fieldByName[fn];
      if (!f) return false;
      return fieldDisplay(f, (entity as Record<string, unknown>)[fn], forwardRefs, cols) !== "";
    });
  };
  const visibleSections = sections.filter(sectionHasContent);

  return (
    <div>
      {region === "main" && metrics.length > 0 && (
        <div className="threeb">
          {metrics.slice(0, 3).map((m, i) => {
            const f = fieldByName[m];
            if (!f) return null;
            return (
              <div key={m} className={`bal ${ACCENTS[i % 3]}`}>
                <div className="k">{sentenceLabel(m)}</div>
                <div className="v tabular">
                  {fieldDisplay(f, (entity as Record<string, unknown>)[m], forwardRefs, cols) || "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visibleSections.map((sec, si) => {
        // №23 Stage C (part 2), D-MS2 rev/D-MS9 — Key Dates: every declared
        // field is a date/datetime carrying (or not) an escalation window.
        // Renders Date/When/State (`ledger-table`, the same flush-card shape
        // Parties/Register/Conflicts already ship) instead of the label:value
        // grid — a per-section shape opt-in (`variant`), not inferred from
        // field types (D-MS8: the engine never guesses). Full stage width:
        // this section lives in `region: "main"`, which — since Stage C part
        // 1 — is C1's card-per-section mechanism, mounted OUTSIDE the narrow
        // `.recordgrid` rail (the width fix this section exists to prove,
        // `MatterCockpitPage.tsx`).
        //
        // R7 ③ GRAIN (decisions-log.md, 15 Aug — corrects this build's first
        // pass): "absent, not empty" is a SECTION-grain rule, never row-grain.
        // A field with no value still gets its row — empty, editable, exactly
        // like the label:value grid below — because D-W2 makes the field
        // itself the way a value gets entered; a row that vanishes when empty
        // can never be filled. The section as a whole still disappears when
        // it has nothing at all (`sectionHasContent`, `visibleSections` above)
        // — that's the correct grain for R7 ③, this isn't.
        if (sec.variant === "dates") {
          const rows = (sec.fields ?? [])
            .map((fn) => {
              const f = fieldByName[fn];
              if (!f) return null;
              const value = (entity as Record<string, unknown>)[fn];
              const result = keyDateState(value, f.attention_days, f.urgent_days);
              const governed =
                governRule && !governRule.except?.includes(fn) ? governRule.correction : undefined;
              const displayText = fieldDisplay(f, value, forwardRefs, cols) || "—";
              return { fn, f, value, displayText, governed, badge: keyDateBadge(result ?? { state: "none", note: "" }) };
            })
            .filter((r): r is NonNullable<typeof r> => r !== null);
          if (rows.length === 0) return null;
          return (
            <div className="card card--flush" key={si}>
              {sec.name && <div className="card__h">{sec.name}</div>}
              <table className="ledger-table">
                <thead><tr><th>Date</th><th>When</th><th>State</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.fn}>
                      <td>{sentenceLabel(r.fn)}</td>
                      <td className="row-date">
                        <InlineFieldCell
                          field={r.f}
                          value={r.value}
                          refs={forwardRefs?.[r.fn]}
                          displayText={r.displayText}
                          governedCorrection={r.governed}
                          onCommit={(v) => commitField(r.fn, v)}
                          onOpenRef={onOpenRef}
                        />
                      </td>
                      <td><span className={r.badge.cls}>{r.badge.label}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // №23 Stage C3 (D-MS3 rev/D-MS9) — Effective rates: resource · rate ·
        // source, full width, source on ONE line. Transfer of the locked
        // reference shape (`tests/snapshots/matter-mockups.ts` `ratesGridHtml`,
        // `d-full-{theme}.png`) onto real `resolveRate` output — read-only
        // here; `set_matter_effective_rate` is reached from the Actions card.
        if (sec.variant === "rates") {
          const rows = rateLadder ?? [];
          if (rows.length === 0) return null;
          return (
            <div className="card card--flush wide" key={si}>
              {sec.name && (
                <div className="card__h">
                  {sec.name} <span className="sub">resolveRate · matter → client → firm</span>
                </div>
              )}
              <table className="ledger-table">
                <thead><tr><th>Resource</th><th>Rate</th><th>Source</th></tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const badge = rateSourceLabel(r);
                    return (
                      <tr key={`${r.resource_kind ?? "label"}:${r.resource_id ?? r.resource_label}`}>
                        <td>{r.resource_label}</td>
                        <td className="tabular">{typeof r.rate_minor === "number" ? `${money(r.rate_minor)}/hr` : "—"}</td>
                        <td><span className={badge.cls}>{badge.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }

        // №23 Stage C3 (D-MS4 rev/D-MS9) — Team: humans and hires in one list,
        // each with a role label and any rate override. Transfer of the locked
        // reference shape (`teamListHtml`) onto real `list_matter_team` rows —
        // read-only here; `add_matter_team_member` is reached from the Actions
        // card. A hire renders identically to a human but for the "hire" badge
        // and the square avatar (`avatar--agent`) — a hire must be VISIBLY
        // present, not a special case with different chrome.
        if (sec.variant === "team") {
          const rows = team ?? [];
          if (rows.length === 0) return null;
          return (
            <div className="card" key={si}>
              {sec.name && (
                <div className="card__h">
                  {sec.name} <span className="sub">assignment records · humans + hires</span>
                </div>
              )}
              <div className="card__b">
                <div className="stack">
                  {rows.map((r) => {
                    const isHire = r.subject_kind === "agent";
                    const name = r.subject_name || (isHire ? r.subject_id : "a colleague");
                    return (
                      <div className="workrow" key={`${r.subject_kind}:${r.subject_id}:${r.matter_role}`}>
                        <span className={`avatar av-md${isHire ? " avatar--agent" : ""}`}>{teamInitials(name)}</span>
                        <div className="meta">
                          <div className="nm">
                            {name}
                            {isHire && <span className="badge badge--soft"> hire</span>}
                          </div>
                          <div className="st">
                            {sentenceLabel(r.matter_role)}
                            {typeof r.rate_override_minor === "number" ? ` · rate override ${money(r.rate_override_minor)}/hr` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="card" key={si}>
            {sec.name && <div className="card__h">{sec.name}</div>}
            <div className={`fields${oneCol ? " one-col" : ""}`}>
              {(sec.fields ?? []).map((fn) => {
                const f = fieldByName[fn];
                if (!f) return null;
                const isStatus = !!statusField && fn === statusField;
                const governed =
                  governRule && !governRule.except?.includes(fn) ? governRule.correction : undefined;
                const displayText =
                  fieldDisplay(f, (entity as Record<string, unknown>)[fn], forwardRefs, cols) || "—";
                return (
                  <div key={fn}>
                    <div className="k">{sentenceLabel(fn)}</div>
                    <div className="v">
                      <InlineFieldCell
                        field={f}
                        value={(entity as Record<string, unknown>)[fn]}
                        refs={forwardRefs?.[fn]}
                        displayText={displayText}
                        governedCorrection={governed}
                        readOnly={isStatus}
                        onCommit={(v) => commitField(fn, v)}
                        onOpenRef={onOpenRef}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
